import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSitrepsV2 } from './hooks/useSitrepsV2';
import { useIncident, useAllIncidents } from '../IAP/hooks/incidentHooks';
import DataSourceChip from './DataSourceChip';
import { useSelector } from 'react-redux';
import {
  useCustomDataSources,
  useCreateCustomDataSource,
  useUpdateCustomDataSource,
  useDeleteCustomDataSource,
} from './hooks/useCustomDataSources';
import CustomDataSourceEditDrawer from './CustomDataSourceEditDrawer';
import {
  useNeoFlag,
  useHighSecurityFlag,
  useTennesseeNationalGuardFlag,
} from '../../utils/featureFlags';
import {
  selectGroup,
  useAppSelector,
  selectAllExerciseWorkspaces,
  selectExerciseWorkspace,
} from 'slices/commonSelectors';
import { useCapacityAnalysisList } from 'hooks/useCapacity';
import { generateInferredName } from './generateInferredName';
import { toast } from 'react-toastify';
import { ChevronDown, ChevronRight, Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group';
import { ScrollArea } from '@/components/ui/scroll-area';
import StylishNewSelect from 'components/DesignSystems/New/StylishNewSelect';
import CustomModal from 'components/DesignSystems/New/CustomModal';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import {
  sourcePrefixes,
  LocationRequiredDataSources,
  ChatWeatherDataSources,
  LayersDataSource,
  SeeristDataSource,
  TakEntitiesDataSource,
  DChatDataSources,
} from './constants/dataSourceConstants';
import { usePanelPosition } from './hooks/usePanelPosition';
import ChatFileSelector from './ChatFileSelector';
import ChatLayerSelector from './ChatLayerSelector';
import {
  DEFAULT_SEERIST_REQUEST,
  SeeristRequestBuilder,
  seeristRequestLabel,
} from './SeeristRequestBuilder';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import {
  clearAiMapScopeFromSelections,
  getLayersAiMapScopeLabel,
} from './utils/addSelectedLayerForAI';
import {
  defaultWebDateRange,
  getWebDateRange,
  setWebDateRange,
  webDateRangeLabel,
} from './chatV2/dataSourceSelections';
import WebDateRangePanel from './chatV2/WebDateRangePanel';

const NO_HIDDEN_SOURCES = [];

const DataSources = ({
  selections = [],
  setSelections,
  usingSitrep = false,
  hiddenSources = NO_HIDDEN_SOURCES,
  hideCustomSources = false,
}) => {
  // Ensure selections is always an array to prevent .find() errors
  if (!Array.isArray(selections)) {
    selections = [];
  }

  const { data: sitreps = [] } = useSitrepsV2();
  const currentSitrepId = useSelector((state) => state.dchat.current_sitrep_id);
  const group_guid = useAppSelector(selectGroup);
  const { data: capacityAnalysisList = [] } = useCapacityAnalysisList(
    group_guid.group_guid
  );
  const { data: currentIncident } = useIncident();
  const { data: allIncidents = [] } = useAllIncidents();
  const currentExercise = useAppSelector(selectExerciseWorkspace);
  const allExercises = useAppSelector(selectAllExerciseWorkspaces) || [];
  const [searchTerm, setSearchTerm] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedCustomSource, setSelectedCustomSource] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [configMode, setConfigMode] = useState(null); // 'sitrep' or 'neo' or null
  const [filesExpanded, setFilesExpanded] = useState(false); // For Files datasource expansion
  const [webExpanded, setWebExpanded] = useState(false);
  const [layersExpanded, setLayersExpanded] = useState(false); // For Layers datasource expansion
  const [seeristExpanded, setSeeristExpanded] = useState(false);
  const panelRef = useRef(null);
  const buttonRef = useRef(null);
  const panelPosition = usePanelPosition(isExpanded, configMode, buttonRef);
  const neoFlag = useNeoFlag();
  const highSecurityFlag = useHighSecurityFlag();
  const tennesseeNationalGuardFlag = useTennesseeNationalGuardFlag();
  const createCustomDataSourceMutation = useCreateCustomDataSource();
  const updateCustomDataSourceMutation = useUpdateCustomDataSource();
  const deleteCustomDataSourceMutation = useDeleteCustomDataSource();
  const isUsingTeams =
    sessionStorage['isUsingTeams'] === true ||
    sessionStorage['isUsingTeams'] === 'true';

  const { data: customDataSources = [] } = useCustomDataSources();
  const { externalLayerTimeFilterById, legendClassFilterByLayerId } =
    useUscgMapContext();

  // Memoize data sources and filtered results
  const { dataSources, regularSources, customSources } = useMemo(() => {
    // Build base data sources
    let fetchedDataSources = [
      { source: 'SITREP', description: 'PRATUS AI SITREPS', id: '' },
      {
        source: 'Files',
        description: `Files uploaded to Pratus${
          isUsingTeams ? ' and SharePoint' : ''
        }`,
        id: '',
      },
    ];

    if (!highSecurityFlag) {
      fetchedDataSources.push({
        source: 'Web',
        description: 'Web Scraping',
        id: '',
      });
    }

    // Hide the Exercise data source from the picker for now — Exercise is
    // being reconfigured and has no corresponding agent tool. Re-enable when
    // the reconfig lands.
    const filteredDChatDataSources = DChatDataSources.filter(
      (source) => source.source !== 'Exercise'
    );

    const locationRequiredSources = tennesseeNationalGuardFlag
      ? LocationRequiredDataSources
      : LocationRequiredDataSources.filter(
          (source) => source.source !== 'TAK Entities'
        );

    if (usingSitrep) {
      fetchedDataSources = fetchedDataSources.filter(
        (source) => source.source !== 'SITREP'
      );
      // Seerist is an automated sitrep section, not an AI data source.
      fetchedDataSources = [
        ...fetchedDataSources,
        ...locationRequiredSources,
        LayersDataSource,
        ...filteredDChatDataSources,
      ];
    } else {
      fetchedDataSources = [
        ...fetchedDataSources,
        ...ChatWeatherDataSources,
        LayersDataSource,
        SeeristDataSource,
        ...(tennesseeNationalGuardFlag ? [TakEntitiesDataSource] : []),
        ...filteredDChatDataSources,
      ];
    }

    const customSources = customDataSources.map((cd) => ({
      source: cd.name,
      description: cd.description,
      id: cd.id,
      isCustom: true,
      customData: cd,
    }));

    if (!hideCustomSources) {
      fetchedDataSources = [...fetchedDataSources, ...customSources];
    }

    if (neoFlag) {
      fetchedDataSources.push({
        source: 'NEO Analysis',
        description: 'Noncombatant Evacuation Order Analysis.',
        id: '',
      });
    }

    if (hiddenSources.length > 0) {
      const hidden = new Set(hiddenSources);
      fetchedDataSources = fetchedDataSources.filter(
        (source) => !hidden.has(source.source)
      );
    }

    // Apply search filter
    let filtered = fetchedDataSources;
    if (searchTerm) {
      filtered = fetchedDataSources.filter(
        (source) =>
          source.source.toLowerCase().includes(searchTerm.toLowerCase()) ||
          source.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return {
      dataSources: fetchedDataSources,
      regularSources: filtered.filter((s) => !s.isCustom),
      customSources: filtered.filter((s) => s.isCustom),
    };
  }, [
    usingSitrep,
    isUsingTeams,
    customDataSources,
    neoFlag,
    searchTerm,
    highSecurityFlag,
    tennesseeNationalGuardFlag,
    hiddenSources,
    hideCustomSources,
  ]);

  // Click outside to close (but not when modals or drawers are open)
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if drawer or modal is open
      if (drawerOpen || showDeleteModal) {
        return;
      }

      // react-select removes elements (clear indicator, options) from the DOM
      // synchronously on mousedown. By the time this listener runs, the click
      // target is detached, so `panelRef.contains()` returns false and the
      // panel would incorrectly close. Treat detached targets as inside the
      // panel — they originated from a React-controlled element that just
      // re-rendered.
      if (!document.contains(event.target)) {
        return;
      }

      // Don't close if clicking on modal or drawer elements
      const isModalClick = event.target.closest(
        '.modal, .modal-backdrop, .offcanvas, .offcanvas-backdrop'
      );

      if (
        panelRef.current &&
        !panelRef.current.contains(event.target) &&
        !isModalClick
      ) {
        setIsExpanded(false);
        setConfigMode(null);
        setFilesExpanded(false);
        setLayersExpanded(false);
        setSeeristExpanded(false);
        setWebExpanded(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        // Don't close panel if drawer is open
        if (drawerOpen) {
          return;
        }
        if (filesExpanded) {
          setFilesExpanded(false);
        } else if (layersExpanded) {
          setLayersExpanded(false);
        } else if (seeristExpanded) {
          setSeeristExpanded(false);
        } else if (webExpanded) {
          setWebExpanded(false);
        } else if (configMode) {
          setConfigMode(null);
        } else if (isExpanded) {
          setIsExpanded(false);
          setConfigMode(null);
        }
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [
    isExpanded,
    configMode,
    filesExpanded,
    layersExpanded,
    seeristExpanded,
    webExpanded,
    drawerOpen,
    showDeleteModal,
  ]);

  const sitrepOptions = sitreps
    .filter((s) => !s.archived)
    .map((sitrep) => ({
      value: sitrep.id,
      label: sitrep.name,
    }));

  const selectedNeo = selections.find(
    (selection) => selection.source === 'NEO Analysis'
  );
  const selectedNeoOption = selectedNeo
    ? (() => {
        const neo = capacityAnalysisList.find(
          (neo) => neo.id === selectedNeo.id
        );
        return neo
          ? {
              value: neo.id,
              label: generateInferredName(neo),
            }
          : null;
      })()
    : null;

  // Incident selection options
  const incidentOptions = allIncidents
    .filter((incident) => !incident.archived)
    .map((incident) => ({
      value: incident.id,
      label: incident.name,
    }));

  // Exercise selection options
  const exerciseOptions = allExercises
    .filter((exercise) => exercise.status === 'Available')
    .map((exercise) => ({
      value: exercise.id,
      label: exercise.displayName,
    }));

  const selectedIncident = selections.find(
    (selection) => selection.source === 'Incident'
  );
  const selectedIncidentOption = selectedIncident
    ? (() => {
        const incident = allIncidents.find(
          (incident) => incident.id === selectedIncident.id
        );
        return incident
          ? {
              value: incident.id,
              label: incident.name,
            }
          : null;
      })()
    : null;

  const selectedExercise = selections.find(
    (selection) => selection.source === 'Exercise'
  );
  const selectedExerciseOption = selectedExercise
    ? (() => {
        const exercise = allExercises.find(
          (exercise) => exercise.id === selectedExercise.id
        );
        return exercise
          ? {
              value: exercise.id,
              label: exercise.displayName,
            }
          : null;
      })()
    : null;

  const handleConfigChange = (sourceType, selectedOption) => {
    const newSelections = selections.filter(
      (selection) => selection.source !== sourceType
    );
    if (selectedOption) {
      newSelections.push({ source: sourceType, id: selectedOption.value });
    }
    setSelections(newSelections);
  };

  const handleToggleSource = (source) => {
    const isSelected = selections.some((s) => s.source === source.source);

    if (isSelected) {
      setSelections(selections.filter((s) => s.source !== source.source));
    } else {
      // For Incident source, auto-select current incident if available
      // For Exercise source, auto-select current exercise if available
      let sourceId = source.id || '';
      if (source.source === 'Incident' && currentIncident?.id) {
        sourceId = currentIncident.id;
      } else if (source.source === 'Exercise' && currentExercise?.id) {
        sourceId = currentExercise.id;
      }

      const nextSelection = {
        source: source.source,
        id: sourceId,
        customData: source.customData,
      };
      if (source.source === 'Seerist') {
        nextSelection.seeristRequest =
          source.seeristRequest || DEFAULT_SEERIST_REQUEST;
      }
      if (source.source === 'Web') {
        nextSelection.webDateRange = defaultWebDateRange();
      }

      setSelections([...selections, nextSelection]);

      // Files / Layers / Seerist / Web configuration UI lives in the panel takeover.
      if (source.source === 'Files') {
        setFilesExpanded(true);
        setLayersExpanded(false);
        setSeeristExpanded(false);
        setWebExpanded(false);
      }
      if (source.source === 'Layers') {
        setLayersExpanded(true);
        setFilesExpanded(false);
        setSeeristExpanded(false);
        setWebExpanded(false);
      }
      if (source.source === 'Seerist') {
        setSeeristExpanded(true);
        setFilesExpanded(false);
        setLayersExpanded(false);
        setWebExpanded(false);
      }
      if (source.source === 'Web') {
        setWebExpanded(true);
        setFilesExpanded(false);
        setLayersExpanded(false);
        setSeeristExpanded(false);
      }
    }
  };

  const handleOpenAddDrawer = () => {
    setSelectedCustomSource(null);
    setDrawerOpen(true);
  };

  const handleOpenEditDrawer = (source) => {
    if (source.source === 'Incident') {
      // For Incident source, open the incident selection config mode
      setConfigMode('incident');
    } else if (source.source === 'Exercise') {
      // For Exercise source, open the exercise selection config mode
      setConfigMode('exercise');
    } else {
      // For custom sources, open the drawer
      setSelectedCustomSource(source.customData);
      setDrawerOpen(true);
    }
  };

  const handleCloseDrawer = () => {
    setSelectedCustomSource(null);
    setDrawerOpen(false);
  };

  const handleSaveDataSource = (dataSourceData) => {
    if (selectedCustomSource) {
      updateCustomDataSourceMutation.mutate(dataSourceData, {
        onSuccess: () => {
          toast.success('Custom data source updated successfully.');
          handleCloseDrawer();
        },
        onError: (error) => {
          console.error('Error updating custom data source:', error);
          toast.error('Failed to update custom data source.');
        },
      });
    } else {
      createCustomDataSourceMutation.mutate(dataSourceData, {
        onSuccess: () => {
          toast.success('Custom data source created successfully.');
          handleCloseDrawer();
        },
        onError: (error) => {
          console.error('Error creating custom data source:', error);
          toast.error('Failed to create custom data source.');
        },
      });
    }
  };

  const handleDeleteCustomSource = (source) => {
    setDeleteId(source.customData.id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (deleteId) {
      deleteCustomDataSourceMutation.mutate(deleteId, {
        onSuccess: () => {
          toast.success('Custom data source deleted successfully.');
          setShowDeleteModal(false);
          setDeleteId(null);
        },
        onError: (error) => {
          console.error('Error deleting custom data source:', error);
          toast.error('Failed to delete custom data source.');
          setShowDeleteModal(false);
          setDeleteId(null);
        },
      });
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  // Handle file selection changes for Files datasource
  const handleFileSelectionChange = (fileIds) => {
    // Update Files datasource in selections with selectedFiles array
    const newSelections = selections.map((sel) =>
      sel.source === 'Files' ? { ...sel, selectedFiles: fileIds } : sel
    );
    setSelections(newSelections);
  };

  // Get selected file IDs from Files datasource
  const getSelectedFileIds = () => {
    const filesSource = selections.find((s) => s.source === 'Files');
    return filesSource?.selectedFiles || [];
  };

  const handleLayerSelectionChange = (selectedLayers) => {
    setSelections((prev) => {
      const hasLayers = prev.some((sel) => sel.source === 'Layers');
      if (!hasLayers) {
        return [...prev, { source: 'Layers', id: '', selectedLayers }];
      }
      return prev.map((sel) =>
        sel.source === 'Layers' ? { ...sel, selectedLayers } : sel
      );
    });
  };

  const getSelectedLayers = () => {
    const layersSource = selections.find((s) => s.source === 'Layers');
    return layersSource?.selectedLayers || [];
  };

  /** Default off unless saved selection already has a datetime window. */
  const getLayersFilterByDatetime = () => {
    const layersSource = selections.find((s) => s.source === 'Layers');
    if (!layersSource) return false;
    if (layersSource.filterByDatetime === true) return true;
    if (layersSource.filterByDatetime === false) return false;
    return (layersSource.selectedLayers || []).some(
      (l) => l?.timeExtent || l?.timeFilterLabel
    );
  };

  const handleLayersFilterByDatetimeChange = (enabled) => {
    setSelections((prev) => {
      const hasLayers = prev.some((sel) => sel.source === 'Layers');
      if (!hasLayers) {
        return [
          ...prev,
          {
            source: 'Layers',
            id: '',
            selectedLayers: [],
            filterByDatetime: enabled,
          },
        ];
      }
      return prev.map((sel) =>
        sel.source === 'Layers' ? { ...sel, filterByDatetime: enabled } : sel
      );
    });
  };

  const handleSeeristRequestChange = (seeristRequest) => {
    setSelections((prev) => {
      const hasSeerist = prev.some((sel) => sel.source === 'Seerist');
      if (!hasSeerist) {
        return [...prev, { source: 'Seerist', id: '', seeristRequest }];
      }
      return prev.map((sel) =>
        sel.source === 'Seerist' ? { ...sel, seeristRequest } : sel
      );
    });
  };

  const getSeeristRequest = () => {
    const seeristSource = selections.find((s) => s.source === 'Seerist');
    return seeristSource?.seeristRequest || DEFAULT_SEERIST_REQUEST;
  };

  // Get workspace ID for file management (incident or exercise)
  const getWorkspaceId = () => {
    return currentIncident?.id || currentExercise?.id || group_guid.group_guid;
  };

  const isSourceSelected = (source) => {
    return selections.some((s) => s.source === source.source);
  };

  const selectedSitrep = usingSitrep
    ? null
    : selections.find((selection) => selection.source === 'SITREP');
  const selectedSitrepOption = selectedSitrep
    ? sitrepOptions.find((option) => option.value === selectedSitrep.id)
    : null;

  // Helper function to get display name for a source
  const getDisplayName = (source) => {
    let displayName = sourcePrefixes[source.source] || source.source;

    if (source.source === 'SITREP' && selectedSitrep?.id) {
      const sitrepName = sitrepOptions.find(
        (opt) => opt.value === selectedSitrep.id
      )?.label;
      if (sitrepName) {
        displayName = `SITREP: ${sitrepName}`;
      }
    } else if (source.source === 'NEO Analysis' && selectedNeo?.id) {
      const neoName = capacityAnalysisList.find(
        (neo) => neo.id === selectedNeo.id
      );
      if (neoName) {
        displayName = `NEO: ${generateInferredName(neoName)}`;
      }
    } else if (source.source === 'Incident' && selectedIncident?.id) {
      const incidentName = incidentOptions.find(
        (opt) => opt.value === selectedIncident.id
      )?.label;
      if (incidentName) {
        displayName = `Incident: ${incidentName}`;
      }
    } else if (source.source === 'Exercise' && selectedExercise?.id) {
      const exerciseName = exerciseOptions.find(
        (opt) => opt.value === selectedExercise.id
      )?.label;
      if (exerciseName) {
        displayName = `Exercise: ${exerciseName}`;
      }
    } else if (source.source === 'Files') {
      const selectedFileIds = getSelectedFileIds();
      const count = selectedFileIds.length;
      displayName = `Files (${count} selected)`;
    } else if (source.source === 'Layers') {
      const selectedLayers = getSelectedLayers();
      const count = selectedLayers.length;
      const filterByDatetime = getLayersFilterByDatetime();
      const timeLabel = filterByDatetime
        ? selectedLayers
            .map(
              (l) =>
                l?.timeFilterLabel || externalLayerTimeFilterById[l?.id]?.label
            )
            .find(Boolean)
        : null;
      const classLabel = selectedLayers
        .map((l) => {
          const fromLayer = l?.legendFilterLabel;
          if (fromLayer) return fromLayer;
          const legend = legendClassFilterByLayerId?.[l?.id];
          if (!legend?.labels?.length && !legend?.values?.length) return null;
          return legend.labels?.length
            ? legend.labels.join(', ')
            : legend.values.map(String).join(', ');
        })
        .find(Boolean);
      const mapScopeLabel = getLayersAiMapScopeLabel(selections);
      const parts = [`Layers (${count} selected)`];
      if (timeLabel) parts.push(`At ${timeLabel}`);
      if (classLabel) parts.push(classLabel);
      if (mapScopeLabel) parts.push(mapScopeLabel);
      displayName = parts.join(' · ');
    } else if (source.source === 'Seerist') {
      const seeristSel = selections.find((s) => s.source === 'Seerist');
      displayName = seeristRequestLabel(seeristSel?.seeristRequest);
    } else if (source.source === 'Web') {
      const label = webDateRangeLabel(getWebDateRange(selections));
      if (label) displayName = `Web (${label})`;
    }

    return displayName;
  };

  // Helper function to check if source needs configuration.
  // Layers/Files only "need config" when already selected but empty — otherwise
  // an unselected click would open the picker without adding the source, and
  // checkbox updates would no-op.
  const needsConfiguration = (source) => {
    return (
      (source.source === 'SITREP' && !usingSitrep && !selectedSitrep?.id) ||
      (source.source === 'NEO Analysis' && !selectedNeo?.id) ||
      (source.source === 'Incident' &&
        !selectedIncident?.id &&
        !currentIncident?.id) ||
      (source.source === 'Exercise' &&
        !selectedExercise?.id &&
        !currentExercise?.id) ||
      (source.source === 'Layers' &&
        isSourceSelected(source) &&
        getSelectedLayers().length === 0) ||
      (source.source === 'Files' &&
        isSourceSelected(source) &&
        getSelectedFileIds().length === 0) ||
      (source.source === 'Seerist' &&
        isSourceSelected(source) &&
        !selections.find((s) => s.source === 'Seerist')?.seeristRequest)
    );
  };

  // Handle configuration click for sources that need it
  const handleConfigureSource = (source) => {
    if (source.source === 'SITREP') {
      setConfigMode('sitrep');
    } else if (source.source === 'NEO Analysis') {
      setConfigMode('neo');
    } else if (source.source === 'Incident') {
      setConfigMode('incident');
    } else if (source.source === 'Exercise') {
      setConfigMode('exercise');
    } else if (source.source === 'Layers') {
      setLayersExpanded(true);
    } else if (source.source === 'Files') {
      setFilesExpanded(true);
    } else if (source.source === 'Seerist') {
      setSeeristExpanded(true);
    } else if (source.source === 'Web') {
      setWebExpanded(true);
    }
  };

  // Render a data source chip with all its conditional logic encapsulated
  const renderCompactChip = (source) => {
    return (
      <DataSourceChip
        key={source.source}
        source={source}
        displayName={getDisplayName(source)}
        isSelected={isSourceSelected(source)}
        onToggle={() => handleToggleSource(source)}
        onEdit={() => handleOpenEditDrawer(source)}
        onDelete={() => handleDeleteCustomSource(source)}
        onConfigure={() => handleConfigureSource(source)}
        needsConfiguration={needsConfiguration(source)}
      />
    );
  };

  const PanelActionButton = ({ onClick, children, primary = false, title }) =>
    usingSitrep ? (
      <Button
        type="button"
        variant={primary ? 'default' : 'outline'}
        size="xs"
        title={title}
        onClick={onClick}
      >
        {children}
      </Button>
    ) : (
      <StylishNewButton
        className={primary ? 'button--primary' : 'button--secondary'}
        onClick={onClick}
        title={title}
      >
        {children}
      </StylishNewButton>
    );

  useEffect(() => {
    if (sitrepOptions.length > 0 && currentSitrepId && !usingSitrep) {
      setSelections((prevSelections) => {
        // Check if SITREP already exists with the correct ID
        const existingSitrep = prevSelections.find(
          (s) => s.source === 'SITREP'
        );

        if (!existingSitrep) {
          // Add new SITREP selection
          return [...prevSelections, { source: 'SITREP', id: currentSitrepId }];
        } else if (existingSitrep.id !== currentSitrepId) {
          // Update existing SITREP with new ID
          return prevSelections.map((s) =>
            s.source === 'SITREP' ? { ...s, id: currentSitrepId } : s
          );
        }

        // No changes needed
        return prevSelections;
      });
    }
  }, [currentSitrepId, sitrepOptions.length, usingSitrep, setSelections]);

  // Auto-select current incident when Incident source is selected but no specific incident is chosen
  useEffect(() => {
    if (currentIncident && incidentOptions.length > 0) {
      setSelections((prevSelections) => {
        // Check if Incident source is selected but has no specific ID
        const existingIncident = prevSelections.find(
          (s) => s.source === 'Incident'
        );

        if (existingIncident && !existingIncident.id) {
          // Update existing Incident selection with current incident ID
          return prevSelections.map((s) =>
            s.source === 'Incident' ? { ...s, id: currentIncident.id } : s
          );
        }

        // No changes needed
        return prevSelections;
      });
    }
  }, [
    currentIncident,
    currentIncident?.id,
    incidentOptions.length,
    setSelections,
  ]);

  // Auto-select current exercise when Exercise source is selected but no specific exercise is chosen
  useEffect(() => {
    if (currentExercise && exerciseOptions.length > 0) {
      setSelections((prevSelections) => {
        // Check if Exercise source is selected but has no specific ID
        const existingExercise = prevSelections.find(
          (s) => s.source === 'Exercise'
        );

        if (existingExercise && !existingExercise.id) {
          // Update existing Exercise selection with current exercise ID
          return prevSelections.map((s) =>
            s.source === 'Exercise' ? { ...s, id: currentExercise.id } : s
          );
        }

        // No changes needed
        return prevSelections;
      });
    }
  }, [
    currentExercise,
    currentExercise?.id,
    exerciseOptions.length,
    setSelections,
  ]);

  // Get selected and unselected sources for rendering
  // Map from selections directly to ensure all selected sources are displayed,
  // even if custom sources haven't finished loading yet
  const selectedSources = selections.map((selection) => {
    const source = dataSources.find((s) => s.source === selection.source) ?? {};
    return {
      description: selection.description,
      isCustom: !!selection.customData,
      ...source,
      ...selection,
    };
  });
  const unselectedRegularSources = regularSources.filter(
    (s) => !isSourceSelected(s)
  );
  const unselectedCustomSources = customSources.filter(
    (s) => !isSourceSelected(s)
  );
  const hasNoResults =
    unselectedRegularSources.length === 0 &&
    unselectedCustomSources.length === 0;

  // Expand Layers/Files into selected-item counts so the pill matches Active
  // labels like "Layers (2 selected)" / "Files (N selected)".
  const selectionDisplayCount = selections.reduce((n, sel) => {
    if (
      sel?.source === 'Layers' &&
      Array.isArray(sel.selectedLayers) &&
      sel.selectedLayers.length > 0
    ) {
      return n + sel.selectedLayers.length;
    }
    if (sel?.source === 'Files') {
      const ids = sel.selectedFiles || sel.selectedFileIds || sel.fileIds;
      if (Array.isArray(ids) && ids.length > 0) return n + ids.length;
    }
    return n + 1;
  }, 0);

  const panelPositionClass = usingSitrep
    ? 'ds-sources-panel--inline'
    : `ds-sources-panel--${panelPosition}`;

  return (
    <div
      className={`ds-data-sources-compact${usingSitrep ? ' ds-data-sources-compact--sitrep' : ''}`}
      ref={panelRef}
    >
      {/* Collapsed Pill Button */}
      {usingSitrep ? (
        <Button
          ref={buttonRef}
          type="button"
          variant={
            isExpanded || selections.length > 0 ? 'secondary' : 'outline'
          }
          size="sm"
          className="tw:rounded-full"
          onClick={() => {
            if (isExpanded) {
              setIsExpanded(false);
              setConfigMode(null);
            } else {
              setIsExpanded(true);
            }
          }}
        >
          {selectionDisplayCount > 0
            ? `${selectionDisplayCount} source${selectionDisplayCount !== 1 ? 's' : ''}`
            : 'Data sources'}
          {isExpanded ? <ChevronDown /> : <ChevronRight />}
        </Button>
      ) : (
        <button
          ref={buttonRef}
          type="button"
          className={`ds-sources-pill ${isExpanded ? 'expanded' : ''} ${selections.length > 0 ? 'has-selections' : ''}`}
          onClick={() => {
            if (isExpanded) {
              setIsExpanded(false);
              setConfigMode(null);
            } else {
              setIsExpanded(true);
            }
          }}
        >
          <span className="ds-pill-text">
            {selectionDisplayCount > 0
              ? `${selectionDisplayCount} source${selectionDisplayCount !== 1 ? 's' : ''}`
              : 'Data sources'}
          </span>
          <span className="ds-pill-arrow">
            <SharedIcon
              iconName={
                isExpanded ? 'keyboard_arrow_down' : 'keyboard_arrow_right'
              }
              size="sm"
              classes="d-flex align-items-center"
            />
          </span>
        </button>
      )}

      {/* Expanded Panel */}
      {isExpanded && (
        <div
          className={
            usingSitrep
              ? `tw:mt-2 tw:flex tw:max-h-[min(420px,50vh)] tw:w-full tw:flex-col tw:overflow-hidden tw:rounded-xl tw:border tw:border-border tw:bg-card tw:text-card-foreground ${configMode || filesExpanded || layersExpanded || seeristExpanded || webExpanded ? 'tw:max-h-[min(520px,60vh)]' : ''}`
              : `ds-sources-panel ${panelPositionClass} ${configMode || filesExpanded || layersExpanded || seeristExpanded || webExpanded ? 'ds-sources-panel--config' : ''}`
          }
        >
          {/* File Selection - Full UI takeover (replaces the data sources UI
              so the two complex views don't fight over height inside one panel). */}
          {filesExpanded ? (
            <div className="ds-config-fullscreen ds-files-fullscreen">
              <div className="ds-config-header">
                <h4
                  className={
                    usingSitrep
                      ? 'tw:m-0 tw:text-sm tw:font-semibold tw:text-foreground'
                      : undefined
                  }
                >
                  Files
                </h4>
                <PanelActionButton onClick={() => setFilesExpanded(false)}>
                  Close
                </PanelActionButton>
              </div>
              <div className="ds-files-fullscreen-body">
                <ChatFileSelector
                  workspaceId={getWorkspaceId()}
                  selectedFileIds={getSelectedFileIds()}
                  onFileSelectionChange={handleFileSelectionChange}
                  userWorkspaceRoleName={undefined}
                />
              </div>
            </div>
          ) : layersExpanded ? (
            <div className="ds-config-fullscreen ds-files-fullscreen">
              <div className="ds-config-header">
                <h4
                  className={
                    usingSitrep
                      ? 'tw:m-0 tw:text-sm tw:font-semibold tw:text-foreground'
                      : undefined
                  }
                >
                  Layers
                </h4>
                <PanelActionButton onClick={() => setLayersExpanded(false)}>
                  Close
                </PanelActionButton>
              </div>
              <div className="ds-files-fullscreen-body p-3">
                <ChatLayerSelector
                  selectedLayers={getSelectedLayers()}
                  onChange={handleLayerSelectionChange}
                  filterByDatetime={getLayersFilterByDatetime()}
                  onFilterByDatetimeChange={handleLayersFilterByDatetimeChange}
                />
              </div>
            </div>
          ) : seeristExpanded ? (
            <div className="ds-config-fullscreen ds-files-fullscreen">
              <div className="ds-config-header">
                <h4
                  className={
                    usingSitrep
                      ? 'tw:m-0 tw:text-sm tw:font-semibold tw:text-foreground'
                      : undefined
                  }
                >
                  Seerist request
                </h4>
                <PanelActionButton onClick={() => setSeeristExpanded(false)}>
                  Close
                </PanelActionButton>
              </div>
              <div
                className="ds-files-fullscreen-body p-3"
                style={{ overflowY: 'auto', maxHeight: '70vh' }}
              >
                <SeeristRequestBuilder
                  value={getSeeristRequest()}
                  onChange={handleSeeristRequestChange}
                />
              </div>
            </div>
          ) : webExpanded ? (
            <div className="ds-config-fullscreen ds-files-fullscreen">
              <div className="ds-config-header">
                <h4
                  className={
                    usingSitrep
                      ? 'tw:m-0 tw:text-sm tw:font-semibold tw:text-foreground'
                      : undefined
                  }
                >
                  Web date range
                </h4>
                <PanelActionButton onClick={() => setWebExpanded(false)}>
                  Close
                </PanelActionButton>
              </div>
              <div className="ds-files-fullscreen-body p-3">
                <WebDateRangePanel
                  value={getWebDateRange(selections)}
                  onChange={(range) =>
                    setSelections((prev) => setWebDateRange(prev, range))
                  }
                />
              </div>
            </div>
          ) : /* Configuration Mode - Full UI takeover */
          configMode ? (
            <div className="ds-config-fullscreen">
              <div className="ds-config-header">
                <h4>
                  {configMode === 'sitrep'
                    ? 'Select PRATUS AI SITREP'
                    : configMode === 'neo'
                      ? 'Select NEO Analysis'
                      : configMode === 'incident'
                        ? 'Select Incident'
                        : 'Select Exercise'}
                </h4>
                <PanelActionButton onClick={() => setConfigMode(null)}>
                  Cancel
                </PanelActionButton>
              </div>
              <div className="ds-config-body">
                {configMode === 'sitrep' ? (
                  sitrepOptions.length ? (
                    <StylishNewSelect
                      options={sitrepOptions}
                      value={selectedSitrepOption || null}
                      onChange={(option) => {
                        handleConfigChange('SITREP', option);
                        setConfigMode(null);
                      }}
                      isClearable={true}
                      placeholder="Choose a PRATUS AI SITREP..."
                      autoFocus
                    />
                  ) : (
                    <span className="ds-config-empty">
                      No PRATUS AI SITREPs available
                    </span>
                  )
                ) : configMode === 'neo' ? (
                  capacityAnalysisList.length ? (
                    <StylishNewSelect
                      options={capacityAnalysisList.map((neo) => ({
                        value: neo.id,
                        label: generateInferredName(neo),
                      }))}
                      value={selectedNeoOption || null}
                      onChange={(option) => {
                        handleConfigChange('NEO Analysis', option);
                        setConfigMode(null);
                      }}
                      isClearable={true}
                      placeholder="Choose a NEO Analysis..."
                      autoFocus
                    />
                  ) : (
                    <span className="ds-config-empty">
                      No NEO Analyses available
                    </span>
                  )
                ) : configMode === 'incident' ? (
                  incidentOptions.length ? (
                    <StylishNewSelect
                      options={incidentOptions}
                      value={selectedIncidentOption || null}
                      onChange={(option) => {
                        handleConfigChange('Incident', option);
                        setConfigMode(null);
                      }}
                      isClearable={true}
                      placeholder="Choose an Incident..."
                      autoFocus
                    />
                  ) : (
                    <span className="ds-config-empty">
                      No Incidents available
                    </span>
                  )
                ) : configMode === 'exercise' ? (
                  exerciseOptions.length ? (
                    <StylishNewSelect
                      options={exerciseOptions}
                      value={selectedExerciseOption || null}
                      onChange={(option) => {
                        handleConfigChange('Exercise', option);
                        setConfigMode(null);
                      }}
                      isClearable={true}
                      placeholder="Choose an Exercise..."
                      autoFocus
                    />
                  ) : (
                    <span className="ds-config-empty">
                      No Exercises available
                    </span>
                  )
                ) : null}
              </div>
            </div>
          ) : (
            /* Normal panel content - shown when not in config mode */
            <>
              {/* Search Bar */}
              {usingSitrep ? (
                <div className="tw:shrink-0 tw:border-b tw:border-border tw:p-2">
                  <InputGroup>
                    <InputGroupAddon>
                      <Search />
                    </InputGroupAddon>
                    <InputGroupInput
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search sources..."
                      autoFocus
                    />
                  </InputGroup>
                </div>
              ) : (
                <div className="ds-panel-search">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search sources..."
                    className="ds-search-input"
                    autoFocus
                  />
                </div>
              )}

              <div
                className={
                  usingSitrep
                    ? 'tw:min-h-0 tw:flex-1 tw:overflow-hidden'
                    : 'ds-panel-content'
                }
              >
                {(() => {
                  const sourcesBody = (
                    <>
                      {/* Active Sources */}
                      {selections.length > 0 && (
                        <div className="ds-active-sources">
                          <div className="ds-mini-label">Active</div>
                          <div className="ds-chips-row">
                            {selectedSources.map((source) => {
                              // Files keeps the same compact pill shape as every
                              // other active source. Clicking it reopens the file
                              // selector takeover; the X removes the source.
                              if (source.source === 'Files') {
                                return (
                                  <DataSourceChip
                                    key="Files"
                                    source={source}
                                    displayName={getDisplayName(source)}
                                    isSelected={true}
                                    onToggle={() => setFilesExpanded(true)}
                                    onDelete={() => handleToggleSource(source)}
                                  />
                                );
                              }
                              if (source.source === 'Layers') {
                                const mapScopeLabel =
                                  getLayersAiMapScopeLabel(selections);
                                return (
                                  <React.Fragment key="Layers">
                                    <DataSourceChip
                                      source={source}
                                      displayName={getDisplayName(source)}
                                      isSelected={true}
                                      onToggle={() => setLayersExpanded(true)}
                                      onDelete={() =>
                                        handleToggleSource(source)
                                      }
                                    />
                                    {mapScopeLabel ? (
                                      <DataSourceChip
                                        source={{ source: 'Layers-map-scope' }}
                                        displayName={mapScopeLabel}
                                        isSelected={true}
                                        onToggle={() =>
                                          setSelections((prev) =>
                                            clearAiMapScopeFromSelections(prev)
                                          )
                                        }
                                      />
                                    ) : null}
                                  </React.Fragment>
                                );
                              }
                              if (source.source === 'Report Section') {
                                // One chip per referenced section (managed by the
                                // report-section picker); the X removes just that
                                // entry.
                                const label = source.includeChildren
                                  ? `${source.name || 'Section'} (all subsections)`
                                  : source.name || 'Section';
                                const removeEntry = () =>
                                  setSelections((prev) =>
                                    (prev || []).filter(
                                      (s) =>
                                        !(
                                          s.source === 'Report Section' &&
                                          s.id === source.id
                                        )
                                    )
                                  );
                                return (
                                  <DataSourceChip
                                    key={`Report Section:${source.id}`}
                                    source={source}
                                    displayName={`Section: ${label}`}
                                    isSelected={true}
                                    onToggle={removeEntry}
                                  />
                                );
                              }
                              if (source.source === 'Seerist') {
                                return (
                                  <DataSourceChip
                                    key="Seerist"
                                    source={source}
                                    displayName={getDisplayName(source)}
                                    isSelected={true}
                                    onToggle={() => setSeeristExpanded(true)}
                                    onDelete={() => handleToggleSource(source)}
                                  />
                                );
                              }
                              if (source.source === 'Web') {
                                return (
                                  <DataSourceChip
                                    key="Web"
                                    source={source}
                                    displayName={getDisplayName(source)}
                                    isSelected={true}
                                    onToggle={() => setWebExpanded(true)}
                                    onDelete={() => handleToggleSource(source)}
                                  />
                                );
                              }
                              return renderCompactChip(source);
                            })}
                          </div>
                        </div>
                      )}

                      {/* Standard Sources - Regular */}
                      <div className="ds-available-sources">
                        <div className="ds-mini-label">Standard</div>
                        <div className="ds-chips-row">
                          {unselectedRegularSources.map((source) =>
                            renderCompactChip(source)
                          )}
                        </div>
                        {hasNoResults && (
                          <div className="ds-no-results">
                            {searchTerm
                              ? `No sources matching "${searchTerm}"`
                              : 'All sources selected'}
                          </div>
                        )}
                      </div>

                      {/* Custom Sources Section */}
                      {!hideCustomSources && (
                        <div className="ds-custom-sources">
                          <div className="ds-mini-label">
                            Custom
                            <PanelActionButton
                              primary
                              onClick={handleOpenAddDrawer}
                              title="Add custom data source"
                            >
                              {usingSitrep ? (
                                <>
                                  <Plus />
                                  Add
                                </>
                              ) : (
                                '+ Add'
                              )}
                            </PanelActionButton>
                          </div>
                          <div className="ds-chips-row">
                            {unselectedCustomSources.map((source) =>
                              renderCompactChip(source)
                            )}
                          </div>
                          {unselectedCustomSources.length === 0 &&
                            customSources.length > 0 && (
                              <div className="ds-no-custom">
                                {searchTerm
                                  ? 'No custom sources match'
                                  : 'All custom sources selected'}
                              </div>
                            )}
                          {customSources.length === 0 && (
                            <div className="ds-no-custom">
                              No custom sources yet
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  );
                  return usingSitrep ? (
                    <ScrollArea className="tw:h-full tw:max-h-80">
                      {sourcesBody}
                    </ScrollArea>
                  ) : (
                    sourcesBody
                  );
                })()}
              </div>
            </>
          )}
        </div>
      )}

      {/* Custom Data Source Edit Drawer */}
      {drawerOpen && (
        <CustomDataSourceEditDrawer
          show={drawerOpen}
          onHide={handleCloseDrawer}
          onSave={handleSaveDataSource}
          dataSource={selectedCustomSource}
        />
      )}

      {/* Delete Confirmation Modal */}
      <CustomModal
        show={showDeleteModal}
        heading="Delete Custom Data Source"
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        saveButton="Delete"
      >
        Are you sure you want to delete this Custom Data Source?
      </CustomModal>
    </div>
  );
};

export default DataSources;
